import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowAltCircleRight,
  faArrowAltCircleLeft,
  faEllipsisH,
} from "@fortawesome/free-solid-svg-icons";
import Sidebar from "./Sidebar";
import { getPopoverByName } from "../../../utils";
import { useDispatch, useSelector } from "react-redux";
import { closePopover, selectSubTab } from "../../../store/common/uiSlice";
import { BASE_URL } from "../../../api/base";
import { setCurrentCollection } from "../../../store/video/videoSlice";
import { UploadImage } from "./UploadImage";
import { useState } from "react";
import { clearData } from "../../../store/customeUplaod/customeUploadSlice";
import { addBackgroundVideo } from "../../../canvasUtils";
import { CanvasContext } from "../../editor/CanvasContext";
import VideoMenu from "../../video/VideoMenu";

const VideoPopup = ({ isOpen, onClose }) => {
  // if (!isOpen) return null;
  const dispatch = useDispatch();
  const thisPopover = getPopoverByName("Video");
  const { canvas } = useContext(CanvasContext);
  const subTab = useSelector((state) => state.ui.subTab);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [openVideoMenuIndex, setOpenVideoMenuIndex] = useState();

  const { bgVideo, collections, currentCollection } = useSelector((state) => ({
    bgVideo: state.video.bgVideo,
    userVideo: state.video.userVideo,
    collections: state.video.collections,
    currentCollection: state.video.currentCollection,
  }));

  const selectedTab = getPopoverByName("Video").tabs[subTab].name;
  const isTemplateVideos = selectedTab === "BG Videos";

  const onTabSelect = useCallback(
    (tabIndex) => {
      const selectedTab = thisPopover.tabs[tabIndex];
      setSearchValue("");
      dispatch(selectSubTab(tabIndex));
      if (selectedTab.collections) {
        dispatch(selectedTab.collections());
      }
      if (selectedTab.images) {
        dispatch(selectedTab.images());
      }
    },
    [dispatch]
  );

  const onSearchBlur = (collectionId) => {
    dispatch(thisPopover.tabs[subTab].images(collectionId));
  };

  const onCollectionSelect = (collectionId, index) => {
    dispatch(setCurrentCollection(index));
    dispatch(thisPopover.tabs[subTab].images(collectionId));
  };

  useEffect(() => {
    // Call onTabSelect with 0 when the component mounts
    onTabSelect(0);
  }, [onTabSelect]);

  const scrollContainer = useRef(null);

  const scrollRight = () => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollBy({ left: 150, behavior: "smooth" });
    }
  };
  const scrollLeft = () => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollBy({ left: -150, behavior: "smooth" });
    }
  };

  const handleMouseEnter = (e) => {
    const { currentTime, paused, ended } = e?.target || {};
    const isPlaying = currentTime && !paused && !ended;
    if (!isPlaying) {
      const vid = e.target;
      vid.muted = true;
      vid.play();
    }
  };
  const handleMouseLeave = (e) => {
    const { currentTime, paused, ended } = e?.target || {};
    const isPlaying = currentTime && !paused && !ended;
    if (isPlaying) {
      const vid = e.target;
      vid.muted = false;
      vid.currentTime = 0;
      vid.pause();
    }
  };

  const handleVideoClick = (url) => {
    dispatch(closePopover());
    return addBackgroundVideo(canvas, { url }, dispatch);
  };
  const isMyMediaCollection = useMemo(
    () => selectedTab === "My BG Video",
    [selectedTab]
  );

  const handleSlideMenu = (index) => {
    return setOpenVideoMenuIndex((prev) =>
      index !== undefined ? (prev === index ? undefined : index) : undefined
    );
  };
  return (
    <div
      className="flex w-full h-full"
      onClick={(e) => {
        e.stopPropagation();
        handleSlideMenu();
      }}
    >
      {open ? (
        <UploadImage
          headerText={"Upload Video"}
          handleBack={() => {
            setOpen(false);
            dispatch(clearData());
          }}
        />
      ) : (
        <>
          <Sidebar
            titleIcon={"/tool/dashboard/static/images/icons/tools/media.svg"}
            title={"Video"}
            tabs={getPopoverByName("Video").tabs}
            onTabSelect={onTabSelect}
            uploadProps={{ label: "upload", onClick: () => setOpen(true) }}
            uploadButton={
              isMyMediaCollection && getPopoverByName("Video").uploadButton
            }
          />
          <div className="p-4 w-4/5 bg-gray-100">
            <div className="mb-2 flex justify-between items-center">
              {!isTemplateVideos && !isMyMediaCollection && (
                <input
                  key={subTab}
                  type="text"
                  placeholder={`Search ${selectedTab}`}
                  className="w-full p-2 border rounded-lg"
                  onBlur={(e) => {
                    if (searchValue !== e.target.value) {
                      setSearchValue(e.target.value);
                      onSearchBlur(e.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (searchValue !== e.target.value) {
                      if (e.key === "Enter" && e.target.value) {
                        setSearchValue(e.target.value);
                        onSearchBlur(e.target.value);
                      }
                    }
                  }}
                />
              )}
            </div>
            {selectedTab !== "My BG Video" ? (
              <div className="mb-3 flex items-center">
                <span
                  className="text-gray-400 cursor-pointer mr-2"
                  onClick={scrollLeft}
                >
                  <FontAwesomeIcon
                    icon={faArrowAltCircleLeft}
                    className="mr-2 text-gray-800 hover:text-vb-pink"
                  />
                </span>
                <div
                  ref={scrollContainer}
                  className="flex overflow-x-auto scroll-container space-x-4 items-center"
                >
                  {(collections || []).map(({ title, id }, index) => (
                    <button
                      key={title}
                      className={`${
                        "capitalize whitespace-nowrap min-w-min px-2 py-1 text-xs text-vb-label-gray2 rounded-full hover:pointer " +
                        (currentCollection === index ? "bg-gray-200" : "")
                      }`}
                      onClick={() => onCollectionSelect(title, index)}
                    >
                      {title}
                    </button>
                  ))}
                </div>

                <span
                  className="text-gray-400 cursor-pointer ml-2 "
                  onClick={scrollRight}
                >
                  <FontAwesomeIcon
                    icon={faArrowAltCircleRight}
                    className="mr-2 text-gray-800 hover:text-vb-pink"
                  />
                </span>
              </div>
            ) : null}
            {!(bgVideo || []).length ? (
              <div className="flex flex-grow h-4/5 justify-center text-sm text-gray-500 items-center text-center w-full">
                No videos to show.
              </div>
            ) : (
              <div className="py-2 grid grid-cols-5 gap-4 w-full h-4/5 overflow-y-scroll">
                {bgVideo.map((image, index) => {
                  const url = (isTemplateVideos ? BASE_URL : "") + image?.video;
                  return (
                    <div
                      key={index}
                      className="relative w-full h-fit rounded flex justify-center items-center cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        handleVideoClick(url);
                      }}
                    >
                      {isMyMediaCollection && (
                        <>
                          <FontAwesomeIcon
                            icon={faEllipsisH}
                            className="cursor-pointer bg-slides-label rounded-sm w-6 h-4  absolute top-2 right-2 text-white z-10 p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              return handleSlideMenu(index);
                            }}
                          />
                          {openVideoMenuIndex === index && (
                            <div className="h-full w-full z-50">
                              <VideoMenu
                                handleClose={handleSlideMenu}
                                id={image.id}
                                slideIndex={index}
                                className="left-[50%] bottom-[50%] w-[188px] h-auto shadow"
                              />
                            </div>
                          )}
                        </>
                      )}
                      <video
                        src={url}
                        alt={`${index}`}
                        className="h-auto max-w-full rounded-lg"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VideoPopup;
